package co.maytes.standupbot.dashboard.features.settings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.feedback.Modal
import co.maytes.standupbot.dashboard.components.widgets.TextInput
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.onKeyDown
import com.varabyte.kobweb.compose.ui.modifiers.onMouseEnter
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.role
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText

private const val SEARCH_WIDTH_PX = 560

@Composable
fun SettingsSearchDialog(
    open: Boolean,
    visible: List<SettingsPageSpec>,
    onDismiss: () -> Unit,
    onPick: (SettingsHit) -> Unit,
) {
    var query by remember(open) { mutableStateOf("") }
    var highlighted by remember(open, query) { mutableStateOf(0) }
    val hits = SettingsCatalog.search(query, visible)

    Modal(
        onDismiss = onDismiss,
        open = open,
        showClose = false,
        widthPx = SEARCH_WIDTH_PX,
        ariaLabel = "Search settings",
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .onKeyDown { event ->
                    when (event.key) {
                        "ArrowDown" -> {
                            event.preventDefault()
                            if (hits.isNotEmpty()) highlighted = (highlighted + 1).coerceAtMost(hits.lastIndex)
                        }
                        "ArrowUp" -> {
                            event.preventDefault()
                            highlighted = (highlighted - 1).coerceAtLeast(0)
                        }
                        "Enter" -> {
                            hits.getOrNull(highlighted)?.let { event.preventDefault(); onPick(it) }
                        }
                    }
                },
        ) {
            TextInput(
                value = query,
                onValueChange = { query = it },
                placeholder = "Search settings",
                ariaLabel = "Search settings",
            )
            Column(modifier = Modifier.margin(top = Spacing.xs).styleModifier { property("gap", "0") }) {
                if (hits.isEmpty()) {
                    SpanText(
                        text = "No settings match “$query”.",
                        modifier = Modifier
                            .padding(Spacing.md)
                            .fontSize(Typography.label.size)
                            .fontWeight(400)
                            .color(Palette.textTertiary),
                    )
                }
                hits.forEachIndexed { index, hit ->
                    SearchRow(hit = hit, selected = index == highlighted, onHover = { highlighted = index }, onPick = { onPick(hit) })
                }
            }
        }
    }
}

@Composable
private fun SearchRow(
    hit: SettingsHit,
    selected: Boolean,
    onHover: () -> Unit,
    onPick: () -> Unit,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(topBottom = Spacing.xs, leftRight = Spacing.sm)
            .borderRadius(Radius.md)
            .cursor(Cursor.Pointer)
            .role("option")
            .let { if (selected) it.backgroundColor(Palette.active) else it }
            .onMouseEnter { onHover() }
            .onClick { onPick() },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        SpanText(
            text = hit.entry.label,
            modifier = Modifier
                .fontSize(Typography.label.size)
                .fontWeight(Typography.label.weight)
                .color(Palette.textPrimary)
                .styleModifier { property("flex", "1 1 auto") },
        )
        SpanText(
            text = hit.trail,
            modifier = Modifier.fontSize(Typography.bodySmall.size).fontWeight(400).color(Palette.textTertiary),
        )
    }
}
